import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRisingEdgeEffect } from 'src/clientLib/hooks'
import { ubemIoApi } from 'src/redux/services/ubem'
import { selectHasNoWarnings } from 'src/redux/slices/analysis/selectors'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import {
  makeSelectTaskStatus,
  makeSelectTaskStatusMeta,
} from 'src/redux/slices/async-processing/selectors'
import { useNavigationEffect } from 'src/redux/slices/navigation/hooks'
import { getProject, getScenarios } from 'src/redux/slices/project/endpoints'

import { Button, Divider, Grid, LinearProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import BarChart from '@material-ui/icons/BarChart'

import ScenarioCreator from './ScenarioCreator'
import ScenarioOverviewRow from './ScenarioOverviewRow'

///// Hooks
const useStyles = makeStyles(() => ({
  divider: {
    marginTop: '1rem',
    marginBottom: '0.5rem',
  },
  title: {
    marginBottom: '1rem',
  },
}))

///// Component
const ScenarioOverview: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { data: scenarios, isLoading: isLoadingScenarios } = getScenarios.useQuery('')
  const { data: project, isLoading: isLoadingProject } = getProject.useQuery('')

  const isLoading = useMemo(
    () => isLoadingScenarios || isLoadingProject,
    [isLoadingScenarios, isLoadingProject],
  )
  const hasBaselineModel = useMemo(() => project?.umi_baseline_file_location, [project])

  const hasScenariosWithData = useMemo(
    () =>
      scenarios?.filter(({ results_umi_file_version }) => results_umi_file_version >= 1)?.length ||
      false,
    [scenarios],
  )

  const scenarioCreatorDisabled = useMemo(() => {
    if (!scenarios) return true
    if (scenarios.length > 4) return true
    return false
  }, [scenarios])

  const processVizFiles = useCallback(() => {
    if (!scenarios) return
    const { project_id } = scenarios[0]
    const body = {
      from_cloud: 'true',
      project_id,
    }

    dispatch(startAsyncTask({ endpoint: AsyncTask.processVizFiles, body }))
  }, [scenarios, dispatch])

  // Navigation Automation
  const { taskLocation: url } =
    useSelector(makeSelectTaskStatusMeta(AsyncTask.processVizFiles))?.data ?? {}
  const result = useSelector(
    makeSelectTaskStatus({
      endpoint: AsyncTask.processVizFiles,
      url: url ?? '',
    }),
  )
  // TODO: Handle warnings from open page
  const hasNoWarnings = useSelector(selectHasNoWarnings)
  const { isSuccess: processingIsSuccess } = result

  useRisingEdgeEffect({
    callback: () => dispatch(ubemIoApi.util.invalidateTags(['Active Project', 'Scenarios'])),
    condition: processingIsSuccess && hasNoWarnings,
  })

  useNavigationEffect({
    destination: '../analysis',
    condition: processingIsSuccess && hasNoWarnings,
  })

  if (isLoading)
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    )
  return (
    <>
      <Divider className={classes.divider} />
      <Grid container className={classes.title}>
        <Grid item xs={12}>
          <Typography variant='h5'>Scenarios</Typography>
        </Grid>

        {!hasBaselineModel && (
          <Grid item xs={12}>
            <Typography variant='body2'>
              You must generate a baseline model from your GIS file before you can create analysis
              scenarios.
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1}>
        {hasBaselineModel && (
          <>
            {scenarios?.map((scenario, i) => (
              <ScenarioOverviewRow scenario={scenario} key={`scenario-${i}-${scenario.title}`} />
            ))}
            {<ScenarioCreator disabled={scenarioCreatorDisabled} />}
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='primary'
                size='small'
                style={{ width: '100%' }}
                disabled={!hasScenariosWithData}
                onClick={processVizFiles}
                endIcon={<BarChart />}
              >
                Open Results Analysis
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default ScenarioOverview
